import { API_IDS } from '@intigral/constant';
import { IHttpAdapter } from '../../entity/interface/iHttpAdapter';
import {
    IThinkAnalyticsSearchSuggestionRequest,
    IThinkAnalyticsSearchSuggestionResponse,
    IThinkAnalyticsSearchPARequest,
    IThinkAnalyticsSearchPAResponse,
} from '../../entity/interface/iThinkAnalyticsSearch';
import { container } from '../../DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';
import { IApiPathManager } from '../config/ApiPathManager';
import { IThinkAnalyticsSearchRepository } from '../../core/repositories/interfaces/iThinkAnalyticsSearchRepository';

class ThinkAnalyticsSearchRepository implements IThinkAnalyticsSearchRepository {
    private static instance: ThinkAnalyticsSearchRepository;

    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
        if (ThinkAnalyticsSearchRepository.instance instanceof ThinkAnalyticsSearchRepository) {
            return ThinkAnalyticsSearchRepository.instance;
        }
        ThinkAnalyticsSearchRepository.instance = this;
    }

    /**
     * Searches for search suggestions from the ThinkAnalytics API.
     *
     * @param payload - The request payload containing the search parameters.
     * @param identityToken - The identity token to authenticate the request.
     * @returns A promise resolving to the search suggestion response.
     */
    async searchSuggestions(
        payload: IThinkAnalyticsSearchSuggestionRequest,
        identityToken: string
    ): Promise<IThinkAnalyticsSearchSuggestionResponse> {
        const path = this.apiPathManager.getPath(API_IDS.THINK_ANALYTICS_API_SEARCH_SUGGESTIONS);
        const params: Record<string, unknown> = {
            apiKey: payload.apiKey,
            productKey: payload.productKey,
            lang: payload.language,
            userName: payload.userName,
            profileName: payload.profileName,
            rating: payload.rating,
            query: payload.query,
        };
        if (!/[?&]q=/.test(path)) {
            params['q'] = payload.query;
        }

        const response = await this.http.get<IThinkAnalyticsSearchSuggestionResponse>(path, {
            params,
            headers: {
                identityToken: identityToken,
            },
        });
        return response.data;
    }

    /**
     * Searches for search PA from the ThinkAnalytics API.
     *
     * @param payload - The request payload containing the search parameters.
     * @param identityToken - The identity token to authenticate the request.
     * @returns A promise resolving to the search PA response.
     */
    async searchPA(
        payload: IThinkAnalyticsSearchPARequest,
        identityToken: string
    ): Promise<IThinkAnalyticsSearchPAResponse> {
        const headerVal: any = {};

        if (identityToken) {
            headerVal['identityToken'] = identityToken;
        } else if (payload?.sessionId) {
            headerVal['sessionId'] = payload.sessionId;
        }

        const path = this.apiPathManager.getPath(API_IDS.THINK_ANALYTICS_API_SEARCH_PA);
        // CMS templates (pa_all_ta) already include q={query}. Spreading payload.q as well
        // made Axios append a second q= after placeholder substitution.
        const { sessionId: _sessionId, q, ...rest } = payload;
        const params: Record<string, unknown> = {
            ...rest,
            apiKey: payload.apiKey,
            query: q,
        };
        if (!/[?&]q=/.test(path)) {
            params['q'] = q;
        }

        const response = await this.http.get<IThinkAnalyticsSearchPAResponse>(path, {
            // withCredentials:true,
            params,
            headers: {
                ...headerVal,
            },
        });
        return response.data;
    }
}
export default ThinkAnalyticsSearchRepository;
